/**
 * HotelsByCategoryTable Component
 * 
 * Universal hotel display component for all package types (DMC, Custom, Manual).
 * Uses unified TABLE layout for all types with consistent headers.
 * 
 * Headers: Destination | Hotel Name | Room Name | Rooms | Nights | Dates
 * 
 * Usage:
 *   <HotelsByCategoryTable
 *     data={hotelsData}
 *     packageType={quotation.route_type}
 *     variant="detail"
 *   />
 */

import React from 'react';
import { Hotel } from 'lucide-react';
import { COMPONENT_VARIANTS } from './displayConfig';

// Normalize star rating to consistent format: "3*", "4*", "5*"
const formatStarRating = (rating) => {
  if (!rating) return null;
  // Convert to string and extract number
  const str = String(rating).trim();
  const match = str.match(/(\d)/);
  if (match) {
    return `${match[1]}*`;
  }
  return null;
};

const HotelsByCategoryTable = ({ 
  data = [],
  packageType = 'custom',
  variant = 'detail',
  showTitle = true,
  className = ''
}) => {
  if (!data || data.length === 0) {
    return null;
  }

  const styles = COMPONENT_VARIANTS[variant] || COMPONENT_VARIANTS.detail;

  // Render a single table for a category/option
  const renderHotelsTable = (hotels, headerName, key) => (
    <div 
      key={key}
      className="border border-gray-200 rounded-lg overflow-hidden"
      data-category={headerName}
    >
      {/* Full-width header bar */}
      <div className={`${styles.headerBg} ${styles.headerText} px-4 py-2 font-semibold`}>
        {headerName}
      </div>

      {/* Mobile stacked cards (<640px) — avoids clipped Room Name column at 360px */}
      <div className="sm:hidden divide-y divide-gray-100">
        {hotels.map((hotel, idx) => (
          <div key={idx} className="p-3 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold text-[var(--navy)] uppercase tracking-wide">
                {hotel.destination}
              </span>
              <span className="text-xs font-medium text-gray-600">{hotel.nights || 1}N</span>
            </div>
            <p className="text-sm font-medium text-[var(--navy)]">
              {hotel.hotelName || 'As per itinerary'}
              {formatStarRating(hotel.starRating) && (
                <span className="text-amber-500 ml-1">({formatStarRating(hotel.starRating)})</span>
              )}
            </p>
            {hotel.roomName && (
              <p className="text-xs text-gray-600">Room: {hotel.roomName}</p>
            )}
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>Rooms: {hotel.rooms || '—'}</span>
              {hotel.dates && <span>{hotel.dates}</span>}
            </div>
          </div>
        ))}
      </div>
      
      {/* Responsive table container */}
      <div className="hidden sm:block overflow-x-auto">
        <table className={`w-full ${styles.fontSize} min-w-[600px]`}>
          <thead>
            <tr className="bg-[color-mix(in_srgb,var(--navy)_10%,white)]">
              <th className={`${styles.padding} text-left border-b border-gray-200 font-semibold sticky left-0 bg-[color-mix(in_srgb,var(--navy)_10%,white)]`}>
                Destination
              </th>
              <th className={`${styles.padding} text-left border-b border-gray-200 font-semibold`}>Hotel Name</th>
              <th className={`${styles.padding} text-center border-b border-gray-200 font-semibold`}>Room Name</th>
              <th className={`${styles.padding} text-center border-b border-gray-200 font-semibold`}>Rooms</th>
              <th className={`${styles.padding} text-center border-b border-gray-200 font-semibold`}>Nights</th>
              <th className={`${styles.padding} text-center border-b border-gray-200 font-semibold`}>Dates</th>
            </tr>
          </thead>
          <tbody>
            {hotels.map((hotel, idx) => (
              <tr 
                key={idx}
                className={idx % 2 === 0 ? 'bg-white' : (variant === 'preview' ? 'bg-slate-50' : 'bg-gray-50')}
              >
                <td className={`${styles.padding} border-b border-gray-200 font-medium sticky left-0 ${idx % 2 === 0 ? 'bg-white' : (variant === 'preview' ? 'bg-slate-50' : 'bg-gray-50')}`}>
                  {hotel.destination}
                </td>
                <td className={`${styles.padding} border-b border-gray-200`}>
                  <span className="font-medium text-[var(--navy)]">
                    {hotel.hotelName || 'As per itinerary'}
                    {formatStarRating(hotel.starRating) && (
                      <span className="text-amber-500 ml-1">({formatStarRating(hotel.starRating)})</span>
                    )}
                  </span>
                </td>
                <td className={`${styles.padding} border-b border-gray-200 text-center text-gray-600`}>
                  {hotel.roomName || '—'}
                </td>
                <td className={`${styles.padding} border-b border-gray-200 text-center text-gray-700`} data-testid={`hotel-rooms-${headerName}-${idx}`}>
                  {hotel.rooms || '—'}
                </td>
                <td className={`${styles.padding} border-b border-gray-200 text-center font-medium`}>
                  {hotel.nights || 1}N
                </td>
                <td className={`${styles.padding} border-b border-gray-200 text-center ${variant === 'preview' ? 'text-[10px]' : 'text-xs'} text-gray-600`}>
                  {hotel.dates || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  // Normalize data to unified format
  // Both optionName (DMC) and category (Custom/Manual) layouts use same table structure
  const normalizedGroups = data.map(item => {
    // DMC options layout: { optionName, hotels[] }
    if (item.optionName !== undefined) {
      return {
        headerName: item.optionName,
        hotels: item.hotels || []
      };
    }
    
    // Category layout: { category, destinations[] } - flatten to hotels array
    if (item.category !== undefined) {
      const flattenedHotels = [];
      (item.destinations || []).forEach(dest => {
        if (dest.hotels && dest.hotels.length > 0) {
          dest.hotels.forEach(h => {
            flattenedHotels.push({
              destination: dest.placeName,
              hotelName: h.hotelName || 'TBC',
              starRating: h.starRating || null,
              roomName: h.roomName || '',
              nights: h.nights || dest.nights,
              rooms: h.rooms,
              dates: h.dates
            });
          });
        } else {
          // No hotel selected for this destination
          flattenedHotels.push({
            destination: dest.placeName,
            hotelName: 'TBC',
            starRating: null,
            roomName: '',
            nights: dest.nights,
            rooms: null,
            dates: null
          });
        }
      });
      
      return {
        headerName: item.category,
        hotels: flattenedHotels
      };
    }
    
    return null;
  }).filter(Boolean);

  return (
    <div 
      className={className}
      data-package-type={packageType}
      data-component="hotels-by-category"
    >
      {showTitle && (
        <h3 className={`font-semibold ${variant === 'preview' ? 'text-sm' : 'text-lg'} mb-3 flex items-center gap-2`}>
          <Hotel className={`${variant === 'preview' ? 'w-4 h-4' : 'w-5 h-5'} text-[var(--navy)]`} />
          Hotels by Category
        </h3>
      )}

      {/* Unified table layout for all package types */}
      <div className="space-y-4">
        {normalizedGroups.map((group, idx) => 
          renderHotelsTable(group.hotels, group.headerName, idx)
        )}
      </div>
    </div>
  );
};

export default HotelsByCategoryTable;
